/**
 * Utility functions for resolving and previewing Safety Inspection Report Headers
 * across Safety Checklist dashboards and Create views.
 */

export const getReportNumberPreview = (
  template,
  projectId = null,
  projectConfigs = {},
) => {
  const config = template?.report_number_config || {};
  const legacyMeta = template?.report_header_meta || {};
  const mode = String(config.mode || "FIXED").toUpperCase();
  const padding = Math.max(1, Number(config.padding || 2));

  const activePid = Number(
    projectId ||
      localStorage.getItem("ACTIVE_PROJECT_ID") ||
      localStorage.getItem("PROJECT_ID") ||
      template?.project_id,
  );

  const isShared = Boolean(
    activePid &&
      template?.project_id &&
      Number(activePid) !== Number(template.project_id),
  );

  // If mode is DYNAMIC_PROJECT, or if this is a shared template from another project:
  if (mode === "DYNAMIC_PROJECT" || isShared) {
    let compPrefix = String(config.company_prefix || "")
      .trim()
      .replace(/-+$/, "");
    let chkCode = String(config.checklist_code || "")
      .trim()
      .replace(/^-+/, "");

    // Fallback extraction from legacy prefix if company/checklist codes were not set separately
    if (!compPrefix || !chkCode) {
      const oldPrefix = String(
        config.prefix || legacyMeta.inspection_report_prefix || "",
      ).trim();
      if (oldPrefix) {
        const parts = oldPrefix.split("-");
        if (parts.length >= 3) {
          if (!compPrefix) compPrefix = parts[0];
          if (!chkCode) chkCode = parts.slice(2).join("-");
        } else if (parts.length === 2) {
          if (!compPrefix) compPrefix = parts[0];
          if (!chkCode) chkCode = parts[1];
        }
      }
    }

    if (!compPrefix) compPrefix = "ADL";
    if (!chkCode) chkCode = "AC";

    let projCode =
      projectConfigs?.[activePid]?.project_code ||
      projectConfigs?.[String(activePid)]?.project_code;

    if (!projCode) {
      // Check cached localStorage configs if available
      try {
        const cached = JSON.parse(
          localStorage.getItem("SAFETY_PROJECT_REPORT_CONFIGS") || "{}",
        );
        projCode = cached[activePid] || cached[String(activePid)];
      } catch {}
    }

    if (!projCode) {
      // Check active project name
      try {
        const rawName =
          localStorage.getItem("ACTIVE_PROJECT_NAME") ||
          localStorage.getItem("PROJECT_NAME") ||
          "";
        if (rawName) {
          const clean = rawName
            .replace(/^Arkade\s*[-–—]\s*/i, "")
            .trim()
            .toUpperCase();
          if (/^[A-Z0-9_-]+$/.test(clean)) {
            projCode = clean;
          }
        }
      } catch {}
    }

    if (projCode) {
      const parts = [compPrefix, projCode, chkCode].filter(Boolean);
      return `${parts.join("-")}-${String(1).padStart(padding, "0")}`;
    }

    const parts = [compPrefix, "PROJECT", chkCode].filter(Boolean);
    return `${parts.join("-")}-${String(1).padStart(padding, "0")}`;
  }

  // FIXED Mode
  const prefix = String(
    config.prefix || legacyMeta.inspection_report_prefix || "",
  )
    .trim()
    .replace(/-+$/, "");

  return prefix
    ? `${prefix}-${String(1).padStart(padding, "0")}`
    : "Auto-generated on submit";
};

/**
 * Resolves the display organization name for internal observation/checklist/permit items,
 * falling back to logged-in user profile/session organization or "Internal".
 */
export const getInternalOrganizationName = (item) => {
  const itemOrg =
    item?.org_name ||
    item?.organization_name ||
    item?.organization?.name ||
    item?.organization?.organization_name ||
    item?.org?.name ||
    item?.org?.organization_name;
  if (
    typeof itemOrg === "string" &&
    itemOrg.trim() &&
    itemOrg.trim() !== "—" &&
    itemOrg.trim() !== "-"
  ) {
    return itemOrg.trim();
  }

  try {
    const rawUserData = localStorage.getItem("USER_DATA");
    if (rawUserData && rawUserData !== "undefined" && rawUserData !== "null") {
      const userData = JSON.parse(rawUserData);
      const name =
        userData?.organization_name ||
        userData?.org_name ||
        userData?.organization?.name ||
        userData?.organization?.organization_name ||
        userData?.company_name ||
        userData?.account?.organization_name;
      if (typeof name === "string" && name.trim()) {
        return name.trim();
      }
    }
  } catch {}

  try {
    const storedName =
      localStorage.getItem("ORGANIZATION_NAME") ||
      localStorage.getItem("org_name") ||
      localStorage.getItem("active_org_name") ||
      localStorage.getItem("active_organization_name");
    if (typeof storedName === "string" && storedName.trim()) {
      return storedName.trim();
    }
  } catch {}

  return "Internal";
};
